import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_URL;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// After login, if the person picked a paid plan on the pricing page,
// run the real Razorpay checkout before they land in the app.
// Cancelling/dismissing just leaves them on the Free plan.
async function runPendingPlanCheckout(user) {
  const plan = sessionStorage.getItem('pending_plan');
  if (!plan || !['starter', 'pro'].includes(plan)) return;
  sessionStorage.removeItem('pending_plan');

  const loaded = await loadRazorpayScript();
  if (!loaded) {
    console.error('Razorpay SDK failed to load');
    return;
  }

  try {
    const orderRes = await authAPI.createOrder({ plan });
    const { orderId, amount, currency, keyId } = orderRes.data;

    await new Promise((resolve) => {
      const rzp = new window.Razorpay({
        key: keyId,
        amount,
        currency,
        name: 'CloudUnify',
        description: `${plan.charAt(0).toUpperCase() + plan.slice(1)} plan`,
        order_id: orderId,
        prefill: { name: user?.name, email: user?.email },
        theme: { color: '#3b82f6' },
        handler: async (response) => {
          try {
            await authAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan,
            });
          } catch (err) {
            console.error('Payment verification failed', err);
          } finally {
            resolve();
          }
        },
        modal: {
          ondismiss: () => resolve(),
        },
      });
      rzp.open();
    });
  } catch (err) {
    console.error('Could not start checkout', err);
  }
}

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('mcp_token');
    const saved = localStorage.getItem('mcp_user');
    if (token && saved) {
      setUser(JSON.parse(saved));
      authAPI.me()
        .then(r => { setUser(r.data.user); localStorage.setItem('mcp_user', JSON.stringify(r.data.user)); })
        .catch(() => logout())
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const r = await authAPI.login({ email, password });
    localStorage.setItem('mcp_token', r.data.token);
    localStorage.setItem('mcp_user',  JSON.stringify(r.data.user));
    setUser(r.data.user);

    await runPendingPlanCheckout(r.data.user);

    // Refresh user in case the plan changed during checkout
    try {
      const fresh = await authAPI.me();
      setUser(fresh.data.user);
      localStorage.setItem('mcp_user', JSON.stringify(fresh.data.user));
    } catch (_) { /* non-fatal */ }

    return r.data;
  };

  const register = async (name, email, password) => {
    const r = await authAPI.register({ name, email, password });
    // Don't auto-login — let user login manually
    return r.data;
  };

  const logout = () => {
    localStorage.removeItem('mcp_token');
    localStorage.removeItem('mcp_user');
    setUser(null);
  };

  const setSession = (token, user) => {
    localStorage.setItem('mcp_token', token);
    localStorage.setItem('mcp_user',  JSON.stringify(user));
    setUser(user);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setSession }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
